import { Controller } from '@hotwired/stimulus'

const SLIDE_WIDTH = 1280
const VIDEO = '[data-provider], iframe, video, turbo-frame[id^="embed_"]'

export default class extends Controller {
  static targets = ['track', 'card', 'counter', 'previous', 'next', 'watchVideo']

  connect() {
    this.index = 0
    this.paintBackgrounds()
    this.offerVideos()
    this.resizeObserver = new ResizeObserver(() => this.render())
    this.resizeObserver.observe(this.element)
    this.exitFullscreen = this.exitFullscreen.bind(this)
    document.addEventListener('fullscreenchange', this.exitFullscreen)
  }

  disconnect() {
    this.resizeObserver.disconnect()
    document.removeEventListener('fullscreenchange', this.exitFullscreen)
  }

  previous(event) {
    this.move(Math.max(this.index - 1, 0), event)
  }

  next(event) {
    this.move(Math.min(this.index + 1, this.lastIndex), event)
  }

  // Page keys only belong to the carousel while they move it; at either end
  // they scroll the page as usual. Keys typed into a field are never ours.
  move(index, event) {
    const { type, target } = event
    if (type === 'keydown' && target.closest?.('input, textarea, select, [contenteditable]')) return
    if (index === this.index) return

    if (type === 'keydown') event.preventDefault()
    const previousIndex = this.index
    this.index = index
    this.stopWatching()
    this.render()
    const source = type === 'keydown' ? 'keyboard' : 'button'
    this.dispatch('changed', { detail: { index, previousIndex, source, totalSlides: this.cardTargets.length } })
  }

  // The click counts as the user gesture browsers need to play with sound.
  watchVideo({ currentTarget }) {
    const card = currentTarget.closest('[data-slide-carousel-target~="card"]')
    card.dataset.watching = ''
    this.players(card).forEach((player) => player.play())
  }

  render() {
    const card = this.cardTargets[this.index]

    // Centre the slide in focus, with a sliver of its neighbours either side,
    // but keep the first and last slides flush with the edges.
    const viewport = this.trackTarget.parentElement.clientWidth
    const centred = card.offsetLeft - this.cardTargets[0].offsetLeft - (viewport - card.offsetWidth) / 2
    const offset = Math.max(Math.min(centred, this.trackTarget.scrollWidth - viewport), 0)
    this.element.style.setProperty('--slide-scale', card.offsetWidth / SLIDE_WIDTH)
    this.trackTarget.style.transform = `translateX(-${offset}px)`

    this.counterLabel.textContent = `${this.index + 1} / ${this.cardTargets.length}`
    this.previousTarget.disabled = this.index === 0
    this.nextTarget.disabled = this.index === this.lastIndex
  }

  // Reveal normally paints data-background-color onto its own background layer
  // and flags dark ones so the theme switches the text to white.
  paintBackgrounds() {
    this.cardTargets.forEach((card) => {
      const section = card.querySelector('[data-background-color]')
      if (!section) return

      card.style.background = section.dataset.backgroundColor
      const [r, g, b] = getComputedStyle(card).backgroundColor.match(/\d+/g).map(Number)
      const dark = (r * 299 + g * 587 + b * 114) / 1000 < 128
      section.classList.add(dark ? 'has-dark-background' : 'has-light-background')
    })
  }

  offerVideos() {
    this.watchVideoTargets.forEach((button) => {
      button.hidden = !button.closest('[data-slide-carousel-target~="card"]').querySelector(VIDEO)
    })
  }

  // Moving on re-locks the slide, so pause its video rather than leave it
  // playing out of sight.
  stopWatching() {
    this.cardTargets.filter((card) => 'watching' in card.dataset).forEach((card) => {
      delete card.dataset.watching
      this.players(card).forEach((player) => player.pause())
    })
  }

  players(card) {
    const videoPlayers = [...card.querySelectorAll('[data-controller~="video-player"]')]
      .map((element) => this.application.getControllerForElementAndIdentifier(element, 'video-player'))
    return [...card.querySelectorAll('video'), ...videoPlayers].filter(Boolean)
  }

  // Watching a video ahead of time is fine; projecting it full screen from the
  // preview is teaching, so undo any fullscreen that starts inside a slide.
  exitFullscreen() {
    if (this.element.contains(document.fullscreenElement)) document.exitFullscreen()
  }

  // ui/tag styles its text on an inner span; writing to the tag would drop it.
  get counterLabel() {
    return this.counterTarget.querySelector('span') || this.counterTarget
  }

  get lastIndex() {
    return this.cardTargets.length - 1
  }
}
